import React, { useEffect, useRef, useState } from "react";
import { ActionIcon, Button, Divider, Image, Loader } from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { useDispatch, useSelector } from "react-redux";
import TrashIcon from "./Icons/TrashIcon";
import CartListFooter from "./CartListFooter";
import { removeArticle } from "../../Actions/articleActions";
import autoAnimate from "@formkit/auto-animate";
import { STATE_CULTURE, STATE_CURRENCY } from "../../Actions/cookiesActions";
import QuantityInput from "./QuantityInput";
import { useMediaQuery } from "@mantine/hooks";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";
import placeholder from "../../../src/Images/placeholder.jpg";
import { sv } from "date-fns/locale/sv";
import { format } from "date-fns";

const useStyles = createStyles((theme) => ({
  mobileCart: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "start",
    alignContent: "center",
    marginTop: "1rem",
  },
  cart: {
    display: "grid",
    alignContent: "start",
    width: "100%",
    height: "fit-content",
    backgroundColor: "rgb(245, 245, 245)",
    padding: "1.5rem 1.75rem",
    boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 12px",
    "@media screen and (max-width: 768px)": {
      gridTemplateColumns: "1fr",
      padding: "1rem",
    },
    "& h2": {
      marginTop: 0,
    },
  },
  cartHeading: {
    width: "fit-content",
    fontSize: "1.25rem",
    fontWeight: "bold",
  },
  mobileButton: {
    paddingLeft: 0,
    color: "#0461b1",
  },
  mobileButtonText: {
    whiteSpace: "normal",
  },
  list: {
    display: "flex",
    flexDirection: "column",
    listStyle: "none",
    padding: 0,
    "& li": {
      height: "100%",
    },
  },
  listWrapper: {
    display: "flex",
    gap: "1rem",
  },
  image: {
    minWidth: "5.6rem",
    objectFit: "cover",
    borderRadius: "8px",
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "100%",
  },
  textWrapper: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
  },
  text: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  nameWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  name: {
    fontWeight: 500,
  },
  ticketOwner: {
    fontWeight: 400,
    marginBottom: "0.3rem",
  },
  price: {
    fontWeight: 500,
  },
  actionWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  bottomWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "end",
  },
  isLoadingWrapper: {
    position: "relative",
    opacity: 0.5,
  },
  loadingWrapper: {
    position: "relative",
  },
  loader: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    margin: "auto",
  },
  hideLabel: {
    position: "absolute",
    width: "0px",
    height: "0px",
    opacity: "0 !important",
  },
  occasionWrapper: {
    display: "flex",
    gap: "0.1rem .75rem",
    flexWrap: "wrap",
  },
  multiTicketWrapper: {
    display: "flex",
    gap: "0.8rem",
    paddingBottom: "0.5rem",
  },
}));

export const removeFromCartTracking = (currency, products, totalPrice) => {
  if (window.dataLayer) {
    window.dataLayer.push({
      event: "remove_from_cart",
      ecommerce: {
        currency,
        value: totalPrice,
        items: products,
      },
    });
  }
};

const NewCart = ({
  saleItems,
  sale,
  saleId,
  invalid,
  noItems,
  paymentMethods,
  providesResponsible,
  setIsOnePageCheckoutActive,
  responsibleData,
  deliveryMethod,
  deliveryMethods,
  enableOptIn,
}) => {
  const { config, language, culture, currency } = useSelector((state) => ({
    config: state.config,
    language: state.language,
    culture: state.cookies[STATE_CULTURE],
    currency: state.cookies[STATE_CURRENCY],
  }));
  const [loading, loadingSet] = useState(false);
  const [show, showSet] = useState(false);
  const { classes } = useStyles();
  const rdxDispatch = useDispatch();
  const cartParent = useRef(null);
  const parent = useRef(null);
  const listParent = useRef(null);
  const locale = culture.substring(0, 2).toLowerCase();
  const image = `${config.apiUri}/articleimage/`;
  const remove = (key, item) => {
    const reducedItem = {
      id: item.article.plu,
      quantity: item.quantity,
      price: item.pricePerPiece.amountInclVat,
      name: item.article.name,
    };
    Promise.resolve()
      .then(() => loadingSet(true))
      .then(() => rdxDispatch(removeArticle(saleId, key)))
      .then(() =>
        removeFromCartTracking(currency, reducedItem, item.totalAmount / 100)
      )
      .then(() => loadingSet(false));
  };
  const isMobile = useMediaQuery("(max-width: 950px)");
  const isDesktop = useMediaQuery("(min-width: 951px)");

  useEffect(() => {
    cartParent.current && autoAnimate(cartParent.current);
  }, [cartParent]);

  useEffect(() => {
    parent.current && autoAnimate(parent.current);
  }, [parent]);

  useEffect(() => {
    listParent.current && autoAnimate(listParent.current);
  }, [listParent]);

  return (
    <div className={`${classes.cart} cart-container`} ref={cartParent}>
      <h3 className={`${classes.cartHeading} cart-heading`}>{language.Cart}</h3>

      {isMobile && (
        <div className={classes.mobileCart}>
          <Button
            variant="subtle"
            className={`${classes.mobileButton} mobileShowMore`}
            rightSection={
              !show ? (
                <BiChevronDown style={{ width: "1.5rem", height: "1.5rem" }} />
              ) : (
                <BiChevronUp style={{ width: "1.5rem", height: "1.5rem" }} />
              )
            }
            onClick={() => showSet(!show)}
          >
            <span className={classes.mobileButtonText}>
              {language.Show} {saleItems?.length} {language.ProductsInCart}
            </span>
          </Button>
        </div>
      )}
      {loading && (
        <div role="alert" aria-live="assertive" className={classes.hideLabel}>
          {language.Removed}
        </div>
      )}
      <div
        className={loading ? classes.isLoadingWrapper : classes.loadingWrapper}
      >
        {loading && <Loader variant="dots" className={classes.loader} />}
        <ul className={classes.list} ref={listParent}>
          {(show || isDesktop) &&
            saleItems &&
            saleItems.map((item, i) => (
              <li key={i}>
                <div className={classes.listWrapper}>
                  <Image
                    fallbackSrc={placeholder}
                    className={classes.image}
                    radius="md"
                    src={`${image}${item.article.imageFilename}`}
                    fit="cover"
                    w={"5.6rem"}
                    h={"5.6rem"}
                    alt={item.article.name}
                  />

                  <div className={classes.wrapper}>
                    <div className={classes.textWrapper}>
                      <div className={classes.text}>
                        <div className={classes.nameWrapper}>
                          <div className={classes.nameWrapper}>
                            <span className={classes.name}>
                              {item.article.name}
                            </span>
                            {item?.ticketOwners?.[0]?.name ? (
                              <small className={classes.ticketOwner}>
                                {item.ticketOwners?.[0]?.name}
                              </small>
                            ) : (
                              <small />
                            )}
                            <div className={classes.occasionWrapper}>
                              {item.occasion ? (
                                <small className={classes.ticketOwner}>
                                  {format(item.occasion, "yyyy-MM-dd HH:mm", {
                                    locale: sv,
                                  })}
                                </small>
                              ) : item.information ? (
                                <small className={classes.ticketOwner}>
                                  {item.information}
                                </small>
                              ) : (
                                ""
                              )}
                              {item?.extraInformation?.length > 0 &&
                                item?.extraInformation.map((info) => (
                                  <small className={classes.ticketOwner}>
                                    {info.value}
                                  </small>
                                ))}
                              <div className={classes.multiTicketWrapper}>
                                {item?.isMultiTicket &&
                                  item?.multiTickets.map((ticket) => (
                                    <small className={classes.ticketOwner}>
                                      {ticket.categoryName} x {ticket.quantity}
                                    </small>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className={classes.actionWrapper}>
                        <ActionIcon
                          color="default"
                          className={`trashButton`}
                          onClick={() => remove(item.key, item)}
                          title={`${language.Cart_RemoveRow} ${language.Cart_Article} ${item.article.name}`}
                          aria-label={`${language.Cart_RemoveRow} ${language.Cart_Article} ${item.article.name}`}
                        >
                          <TrashIcon />
                        </ActionIcon>
                      </div>
                    </div>
                    <div className={classes.bottomWrapper}>
                      <QuantityInput
                        item={item}
                        language={language}
                        quantity={item.quantity}
                        saleId={saleId}
                        disabled={!item.canChangeQuantity}
                      />
                      <span className={classes.price}>
                        {item.totalPriceInclVat}
                      </span>
                    </div>
                  </div>
                </div>
                <Divider my="lg" style={{ borderTopColor: "#E7E9EC" }} />
              </li>
            ))}
        </ul>
        <CartListFooter
          sale={sale}
          saleId={sale.id}
          itemQuantity={sale.itemQuantity}
          invalid={invalid}
          noItems={noItems}
          paymentMethods={paymentMethods}
          providesResponsible={providesResponsible}
          setIsOnePageCheckoutActive={setIsOnePageCheckoutActive}
          responsibleData={responsibleData}
          deliveryMethod={deliveryMethod}
          deliveryMethods={deliveryMethods}
          enableOptIn={enableOptIn}
        />
      </div>
    </div>
  );
};

export default NewCart;
